import React from 'react';
import { Box, Flex, Link, Avatar, Icon } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { LinkExternal01 } from '@kula-ai/icons/dual-tone/LinkExternal01';
import { DetailSection } from './DetailSection';
// import { UserInfoSection } from './UserInfoSection';
import dayjs from 'dayjs';
import {
  constructEquityLabel,
  formatOfferSalary,
  formatSalaryRange,
  isSalaryAboveRange,
} from '@/utils/offerUtils';
import { AboveRangeTag } from '@/components/ats/OfferInitiation/AboveRangeTag';

interface AssociatedCandidateSectionProps {
  requisitionAssociation?: {
    id: number;
    associable_type: 'AtsApplication' | 'AtsOffer';
    type: 'AtsApplication' | 'AtsOffer';
    application?: {
      id: number;
      candidate: {
        id: number;
        name: string;
        email: string;
        avatar_url?: string | null;
      };
    };
    offer?: {
      id: number;
      offer_status: string;
      salary?: number | null;
      salary_frequency: string;
      additional_compensation?: number | null;
      start_date?: string | null;
      expiration_date?: string | null;
      job_title: string;
      candidate_name: string;
      candidate_email: string;
      application: {
        id: number;
      };
      candidate: {
        id: number;
        name: string;
        email: string;
        avatar_url?: string | null;
      };
      salary_currency?: {
        id: number;
        currency_code: string;
        currency_name: string;
        currency_symbol: string;
      };
      additional_compensation_currency?: {
        id: number;
        currency_code: string;
        currency_name: string;
        currency_symbol: string;
      };
      ats_equity_award?: {
        amount?: string;
        cliff_of_vesting?: string;
        unit_of_cliff?: string;
        period_of_vesting?: string;
        unit_of_vesting_period?: string;
        cycles_after_cliff?: string;
        unit_of_cycle_after_cliff?: string;
        currency?: {
          id: number;
          currency: string;
          currency_code: string;
          currency_name: string;
          currency_symbol: string;
        };
      };
    };
  };
  jobId?: number;
  dateFormat: string;
  filledBy?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url?: string;
  };
  requisitionSalaryRange?: {
    minimum_salary?: number;
    maximum_salary?: number;
    salary_currency?: string;
  };
}

export const AssociatedCandidateSection: React.FC<AssociatedCandidateSectionProps> =
  ({
    requisitionAssociation,
    jobId,
    dateFormat,
    filledBy,
    requisitionSalaryRange,
  }) => {
    if (!requisitionAssociation) {
      return null;
    }

    const formatDate = (date?: string | null) => {
      if (!date) return '-';
      return dayjs(date).format(dateFormat);
    };

    // Check if offer salary exceeds requisition salary range
    const offer = requisitionAssociation.offer;
    const { minimum_salary, maximum_salary, salary_currency } =
      requisitionSalaryRange || {};
    const reqCurrency = salary_currency || 'USD';
    const shouldShowAboveRange =
      requisitionSalaryRange &&
      isSalaryAboveRange(offer?.salary, maximum_salary);

    const salaryRangeText =
      minimum_salary !== undefined && maximum_salary !== undefined
        ? formatSalaryRange(minimum_salary, maximum_salary, reqCurrency)
        : '';

    // Determine candidate and application ID
    let candidate;
    let applicationId;

    if (requisitionAssociation.type === 'AtsApplication') {
      candidate = requisitionAssociation.application?.candidate;
      applicationId = requisitionAssociation.application?.id;
    } else if (requisitionAssociation.type === 'AtsOffer') {
      candidate = requisitionAssociation.offer?.candidate;
      applicationId = requisitionAssociation.offer?.application.id;
    }

    if (!candidate || !jobId) {
      return null;
    }

    const candidateUrl = `/jobs/${jobId}?appId=${applicationId}&currentView=stage&tab=candidates`;

    return (
      <Box>
        <DetailSection label="Associated candidate">
          <Flex direction="column" gap={4}>
            <Flex width="fit-content">
              <Link
                href={candidateUrl}
                display="flex"
                alignItems="center"
                gap="3px"
                isExternal
                outline="none"
                color="primary-500"
              >
                <Avatar
                  size="xs"
                  width="18px"
                  height="18px"
                  borderRadius="xs"
                  className="avatar-xs"
                  name={candidate.name}
                  src={candidate.avatar_url || undefined}
                />
                <KText
                  fontSize="sm"
                  fontWeight="medium"
                  _hover={{ textDecoration: 'underline' }}
                >
                  {candidate.name}
                </KText>
                <Icon as={LinkExternal01} boxSize="0.75rem" />
              </Link>
            </Flex>

            {/* Offer Details - Only for AtsOffer type */}
            {requisitionAssociation.type === 'AtsOffer' &&
              requisitionAssociation.offer && (
                <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
                  <Flex direction="column" gap={1} minWidth="0" flex="1.8">
                    <KText color="primary-900" fontWeight="medium">
                      Salary
                    </KText>
                    <Flex alignItems="center" gap={2}>
                      <KText
                        isTruncated
                        enableTooltip
                        tooltipText={formatOfferSalary(
                          requisitionAssociation.offer.salary,
                          requisitionAssociation.offer.salary_currency
                        )}
                      >
                        {formatOfferSalary(
                          requisitionAssociation.offer.salary,
                          requisitionAssociation.offer.salary_currency
                        )}
                      </KText>
                      {shouldShowAboveRange && salaryRangeText && (
                        <AboveRangeTag salaryRangeText={salaryRangeText} />
                      )}
                    </Flex>
                  </Flex>
                  <Flex direction="column" gap={1} minWidth="0" flex="1">
                    <KText color="primary-900" fontWeight="medium">
                      Start date
                    </KText>
                    <KText
                      isTruncated
                      enableTooltip
                      tooltipText={formatDate(
                        requisitionAssociation.offer.start_date
                      )}
                    >
                      {formatDate(requisitionAssociation.offer.start_date)}
                    </KText>
                  </Flex>
                  <Flex direction="column" gap={1} minWidth="0" flex="1">
                    <KText color="primary-900" fontWeight="medium">
                      Role
                    </KText>
                    <KText
                      isTruncated
                      enableTooltip
                      tooltipText={
                        requisitionAssociation.offer.job_title || '-'
                      }
                    >
                      {requisitionAssociation.offer.job_title || '-'}
                    </KText>
                  </Flex>
                  <Flex direction="column" gap={1} minWidth="0" flex="1">
                    <KText color="primary-900" fontWeight="medium">
                      Equity
                    </KText>
                    <KText
                      isTruncated
                      enableTooltip
                      tooltipText={
                        constructEquityLabel(
                          requisitionAssociation.offer.ats_equity_award
                        ) || '-'
                      }
                    >
                      {constructEquityLabel(
                        requisitionAssociation.offer.ats_equity_award
                      ) || '-'}
                    </KText>
                  </Flex>
                </Flex>
              )}

            {/* Filled By User Info */}
            {/* Todo enable later if needed */}
            {/* {filledBy && (
              <DetailSection label="Filled by">
                <UserInfoSection user={filledBy} />
              </DetailSection>
            )} */}
          </Flex>
        </DetailSection>
      </Box>
    );
  };
